import reportApi from '@/api/report'
import { generateFormHandlerMutations } from '@/helpers/forms'

const pad = value => String(value).padStart(2, '0')

const asDate = date => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

const daysAgo = days => {
  const date = new Date()
  date.setDate(date.getDate() - days)

  return asDate(date)
}

const emptyTotals = () => ({
  paidCount: 0, paidAmount: 0, problemCount: 0, problemAmount: 0,
})

const emptyCaseTotals = () => ({ newCount: 0, inProgressCount: 0, resolvedCount: 0 })

const caseCounter = {
  New: 'newCount',
  InProgress: 'inProgressCount',
  Resolved: 'resolvedCount',
}

const state = {
  rows: [],
  categories: [],
  branches: [],
  operators: [],
  restaurants: [],
  totals: emptyTotals(),

  caseTotals: emptyCaseTotals(),

  checkedAt: null,

  loading: false,
  downloading: false,
  loaded: false,
  error: null,

  filters: {
    from: daysAgo(6),
    to: daysAgo(0),
    restaurantId: null,

    caseStatus: null,
  },
}

const payload = () => ({
  from: state.filters.from,
  to: state.filters.to,
  restaurantId: state.filters.restaurantId || null,
  caseStatus: state.filters.caseStatus || null,
})

const fileName = response => {
  const disposition = response.headers['content-disposition'] || ''
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)

  if (encoded) return decodeURIComponent(encoded[1])

  const plain = /filename="?([^";]+)"?/i.exec(disposition)

  return plain ? plain[1] : 'Сверка оплат.xlsx'
}

const getters = {}

const actions = {
  fetch({ commit }) {
    commit('LOADING')

    return reportApi.getPaymentReconciliation(payload())
      .then(({ data }) => commit('SET_REPORT', data))
      .catch(error => commit('SET_ERROR', error))
  },

  download({ commit }) {
    commit('DOWNLOADING')

    return reportApi.getPaymentReconciliationFile(payload())
      .then(response => commit('SAVE_FILE', response))
      .catch(error => commit('SET_ERROR', error))
  },

  fetchCase(context, orderId) {
    return reportApi.getPaymentReconciliationCase(orderId).then(({ data }) => data)
  },

  setCaseStatus({ commit }, { orderId, status }) {
    return reportApi.setPaymentReconciliationCaseStatus(orderId, status)
      .then(({ data }) => {
        commit('APPLY_CASE', data)

        return data
      })
  },

  addCaseComment({ commit }, { orderId, text }) {
    return reportApi.addPaymentReconciliationComment(orderId, text)
      .then(({ data }) => {
        commit('APPLY_CASE', data)

        return data
      })
  },
}

const mutations = {
  LOADING(state) {
    state.loading = true
    state.error = null
  },

  DOWNLOADING(state) {
    state.downloading = true
    state.error = null
  },

  SET_REPORT(state, report) {
    state.rows = report.rows || []
    state.categories = report.categories || []
    state.branches = report.branches || []
    state.operators = report.operators || []
    state.restaurants = report.restaurants || []
    state.totals = report.totals || emptyTotals()
    state.caseTotals = report.caseTotals || emptyCaseTotals()
    state.checkedAt = report.checkedAt || null
    state.loading = false
    state.loaded = true
  },

  SET_ERROR(state, error) {
    const response = error && error.response
    const data = response && response.data

    state.error = (data && (data.message || data.title)) || 'Не удалось построить отчёт'
    state.loading = false
    state.downloading = false
  },

  APPLY_CASE(state, view) {
    const row = state.rows.find(item => item.orderId === view.orderId)

    if (!row) return

    if (row.caseStatus !== view.status) {
      if (caseCounter[row.caseStatus]) state.caseTotals[caseCounter[row.caseStatus]] -= 1
      if (caseCounter[view.status]) state.caseTotals[caseCounter[view.status]] += 1
    }

    row.caseStatus = view.status
    row.caseStatusName = view.statusName
    row.commentsCount = view.commentsCount
    row.lastComment = view.lastComment
  },

  SAVE_FILE(state, response) {
    const url = window.URL.createObjectURL(response.data)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', fileName(response))
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
    state.downloading = false
  },

  ...generateFormHandlerMutations(state),
}

const modules = {}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
  modules,
}
